import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import Guard from "../../components/access-control/rbac/Guard";
import { careService } from "../../services/careService";
import { useWorkspace } from "../../contexts/WorkspaceContext";

const RESIDENT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join("").toUpperCase() || "--";
const fullNameFor = (resident = {}) =>
  resident.name ||
  resident.fullName ||
  resident.residentName ||
  [resident.firstName, resident.middleName, resident.lastName].filter(Boolean).join(" ") ||
  "Unnamed resident";
const locationFor = (resident = {}) => resident.locationInformation || resident.address || resident.location || {};
const normalizeResident = (resident = {}) => {
  const name = fullNameFor(resident);
  const location = locationFor(resident);
  return {
    id: resident.id || resident.residentId,
    initials: resident.initials || initialsFor(name),
    name,
    preferredName: resident.preferredName || resident.nickname || "",
    gender: resident.gender || resident.sex || "",
    city: location.city || resident.city || "",
    state: location.state || resident.state || "",
    room: resident.room || resident.roomNumber || "",
    ncpStatus: resident.ncpStatusLabel || resident.ncpStatus || resident.carePlanStatus || "Current",
    alerts: Number(resident.alerts || resident.openAlerts || resident.openAlertCount || 0),
    status: String(resident.status || "").toUpperCase(),
    onboardingStatus: String(resident.onboardingStatus || resident.onboardingStepStatus?.status || "").toUpperCase(),
  };
};

const residentHref = (resident) => `/residents/${resident.id || resident.name}`;

export default function ResidentsListPage() {
  const { selectedWorkspaceId, workspaceListReady } = useWorkspace();
  const [residents, setResidents] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    if (!workspaceListReady) {
      setLoading(true);
      return () => {
        active = false;
      };
    }
    if (!selectedWorkspaceId) {
      setResidents([]);
      setError("Select a workspace to view residents.");
      setLoading(false);
      return () => {
        active = false;
      };
    }
    setLoading(true);
    careService.residents.listMini({
      workspaceId: selectedWorkspaceId,
      wid: selectedWorkspaceId,
      size: 100,
    })
      .then((data) => {
        if (!active) return;
        setResidents((Array.isArray(data) ? data : []).map(normalizeResident));
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setResidents([]);
        setError(err?.response?.data?.message || err?.message || "Unable to load residents.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [selectedWorkspaceId, workspaceListReady]);

  const totalAlerts = residents.reduce((sum, resident) => sum + resident.alerts, 0);
  const dueSoon = residents.filter((resident) => String(resident.ncpStatus).toUpperCase() !== "CURRENT").length;
  const visibleResidents = residents;
  const totalPages = Math.max(1, Math.ceil(visibleResidents.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginatedResidents = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return visibleResidents.slice(startIndex, startIndex + pageSize);
  }, [visibleResidents, currentPage, pageSize]);
  const paginationStart = visibleResidents.length ? ((currentPage - 1) * pageSize) + 1 : 0;
  const paginationEnd = Math.min(visibleResidents.length, currentPage * pageSize);

  useEffect(() => {
    setPage(1);
  }, [pageSize]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <CareShell className="dashboard-content residents-page">
      <div className="residents-page-head">
        <div className="page-title">
          <h1>Facility Residents</h1>
          <p>Residents currently assigned to Sunset Gardens Assisted Living.</p>
        </div>
        <div className="residents-actions">
          <button className="mini-btn"><i className="ti ti-filter" /> Filter</button>
          <Guard roles={["care_owner", "care_admin"]}>
            <Link className="btn primary" to="/residents/new"><i className="ti ti-user-plus" /> Onboard New Resident</Link>
          </Guard>
        </div>
      </div>

      <div className="resident-summary-grid">
        {[
          ["ti-users", "Total residents", residents.length, "In this workspace"],
          ["ti-heart-handshake", "NCP follow-up", dueSoon, "Need review or update"],
          ["ti-alert-triangle", "Open alerts", totalAlerts, "Across all residents"],
          ["ti-notes", "Daily notes", "24", "Captured this week"],
        ].map(([icon, label, value, note]) => (
          <article className="resident-summary-card" key={label}>
            <span className="tiny-icon"><i className={`ti ${icon}`} /></span>
            <div><strong>{label}</strong><b>{value}</b><small>{note}</small></div>
          </article>
        ))}
      </div>

      <Panel title="Residents">
        {error ? <p className="form-error">{error}</p> : null}
        <div className="resident-list-toolbar">
          <label className="resident-search">
            <i className="ti ti-search" />
            <input placeholder="Search residents by name, room, contact, or care team" />
          </label>
          <div className="resident-filter-pills">
            <button className="is-active">All</button>
            <button>NCP Due</button>
            <button>Alerts</button>
            <button>Active</button>
          </div>
        </div>

        <div className="resident-directory">
          <div className="resident-directory-row resident-directory-head">
            <span>Resident</span><span>Preferred name</span><span>Gender</span><span>City</span><span>State</span><span>Status</span><span />
          </div>
          {loading ? <p>Loading residents...</p> : null}
          {!loading && !visibleResidents.length ? <p>No residents found.</p> : null}
          {paginatedResidents.map((resident) => (
            <Link className="resident-directory-row" to={residentHref(resident)} key={resident.id || resident.name}>
              <span className="resident-directory-person">
                <b>{resident.initials}</b>
                <span><strong>{resident.name}</strong><small>{resident.room ? `Room ${resident.room}` : "No room assigned"}</small></span>
              </span>
              <span>{resident.preferredName || "Not added"}</span>
              <span>{resident.gender || "Not added"}</span>
              <span>{resident.city || "Not added"}</span>
              <span>{resident.state || "Not added"}</span>
              <span><em className={`pill ${resident.status === "ACTIVE" ? "low" : resident.status === "PROSPECTIVE" ? "blue" : "medium"}`}>{resident.status || "Unknown"}</em></span>
              <span className="resident-row-actions">{resident.alerts ? <em>{resident.alerts}</em> : null}<i className="ti ti-chevron-right" /></span>
            </Link>
          ))}
        </div>
        {visibleResidents.length ? (
          <div className="timesheet-pagination resident-pagination">
            <span>
              Showing <strong>{paginationStart}-{paginationEnd}</strong> of <strong>{visibleResidents.length}</strong>
            </span>
            <label>
              Rows
              <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
                {RESIDENT_PAGE_SIZE_OPTIONS.map((size) => (
                  <option value={size} key={size}>{size}</option>
                ))}
              </select>
            </label>
            <div className="timesheet-pagination-actions">
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.max(1, current - 1))}
                disabled={currentPage <= 1}
              >
                <i className="ti ti-chevron-left" /> Previous
              </button>
              <strong>Page {currentPage} of {totalPages}</strong>
              <button
                className="mini-btn"
                type="button"
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                disabled={currentPage >= totalPages}
              >
                Next <i className="ti ti-chevron-right" />
              </button>
            </div>
          </div>
        ) : null}
      </Panel>
    </CareShell>
  );
}
